import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus,
  Trash2,
  Save,
  AlertCircle,
  Check,
  Download,
  Eye,
  Home,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import { PageHero, PageShell } from "@/components/PageShell";
import { InlineHint, PageLoadingState } from "@/components/PageStates";
import { getTodayInBusinessTz } from "@/lib/date";
import SearchableDropdown from "@/components/SearchableDropdown";
import { getFieldErrorClass, getFieldMessageClass } from "@/lib/form-ui";
import { handleArrowFieldKeyDown } from "@/lib/keyboardNav";
import { formatStockValue } from "@/lib/currency";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PEMESANAN_DRAFT_KEY_PREFIX = "pemesanan-draft";
const HISTORY_PAGE_SIZE = 5;

const getTomorrowDate = () => {
  const todayStr = getTodayInBusinessTz();
  const tomorrow = new Date(`${todayStr}T00:00:00`);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
};

const getPemesananDownloadFilename = (pemesanan) => {
  if (!pemesanan || typeof pemesanan !== "object") {
    return "Pemesanan.pdf";
  }

  let penerimaanDate;
  if (pemesanan.tanggal_penerimaan) {
    penerimaanDate = new Date(`${pemesanan.tanggal_penerimaan}T00:00:00`);
  } else {
    penerimaanDate = new Date(pemesanan.tanggal);
    if (!Number.isNaN(penerimaanDate.getTime())) {
      penerimaanDate.setDate(penerimaanDate.getDate() + 1);
    }
  }

  if (Number.isNaN(penerimaanDate.getTime())) {
    return "Pemesanan.pdf";
  }

  const tanggalLabel = penerimaanDate
    .toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
    .toLowerCase();

  return `Pemesanan untuk tanggal ${tanggalLabel}.pdf`;
};

const getBarangUnits = (barang) => {
  const units = Object.keys(barang?.konversi_satuan || {});
  if (barang?.satuan && !units.includes(String(barang.satuan).toLowerCase())) {
    units.push(String(barang.satuan).toLowerCase());
  }
  return units;
};

const getBarangKonversiFactor = (barang, satuanInput) => {
  const selectedUnit = String(satuanInput || "").toLowerCase();
  const baseUnit = String(barang?.satuan || "").toLowerCase();
  const map = barang?.konversi_satuan || {};
  return Number(map[selectedUnit] ?? (selectedUnit === baseUnit ? 1 : NaN));
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((entry) => {
        if (typeof entry === "string") {
          return entry;
        }
        if (!entry || typeof entry !== "object") {
          return "";
        }

        const message = typeof entry.msg === "string" ? entry.msg : "";
        const location = Array.isArray(entry.loc)
          ? entry.loc
              .filter((locPart) => typeof locPart === "string" || typeof locPart === "number")
              .join(".")
          : "";

        if (location && message) {
          return `${location}: ${message}`;
        }
        return message;
      })
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join("; ");
    }
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message;
  }

  return fallbackMessage;
};

const PemesananPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [barangMenipis, setBarangMenipis] = useState([]);
  const [allBarang, setAllBarang] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [kategori, setKategori] = useState([]);
  const [pemesananList, setPemesananList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPemesanan, setSelectedPemesanan] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [formData, setFormData] = useState({
    keterangan: "",
    tanggal_penerimaan: getTomorrowDate(),
  });
  const [newItem, setNewItem] = useState({
    barang_id: "",
    qty: "",
    satuan_input: "",
    keterangan: "",
  });
  const [searchManualBarang, setSearchManualBarang] = useState("");
  const [selectedKategoriId, setSelectedKategoriId] = useState("all");
  const [stokMenipisQtyMap, setStokMenipisQtyMap] = useState({});
  const [stokMenipisUnitMap, setStokMenipisUnitMap] = useState({});
  const [draftReady, setDraftReady] = useState(false);
  const [pemesananOffset, setPemesananOffset] = useState(0);
  const [totalPemesanan, setTotalPemesanan] = useState(0);
  const [hasMorePemesanan, setHasMorePemesanan] = useState(false);
  const [loadingMorePemesanan, setLoadingMorePemesanan] = useState(false);
  const [historySearchTerm, setHistorySearchTerm] = useState("");
  const [historyStatusFilter, setHistoryStatusFilter] = useState("all");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    confirmText: "Ya",
    destructive: false,
    onConfirm: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [manualItemErrors, setManualItemErrors] = useState({});
  const draftKey = `${PEMESANAN_DRAFT_KEY_PREFIX}:${user?.username || "guest"}`;
  const barangInputRef = useRef(null);
  const manualQtyInputRef = useRef(null);
  const manualSatuanSelectRef = useRef(null);
  const manualAddButtonRef = useRef(null);
  const quickAddQtyRefs = useRef(new Map());
  const quickAddSatuanRefs = useRef(new Map());
  const quickAddButtonRefs = useRef(new Map());
  const pemesananTanggalInputRef = useRef(null);
  const pemesananKeteranganRef = useRef(null);
  const pemesananCancelButtonRef = useRef(null);
  const pemesananConfirmButtonRef = useRef(null);

  const focusFieldNextFrame = useCallback((fieldRef) => {
    requestAnimationFrame(() => {
      fieldRef.current?.focus();
    });
  }, []);

  const setMappedRef = useCallback((refMap, key, node) => {
    if (!key) {
      return;
    }

    if (node) {
      refMap.current.set(key, node);
      return;
    }

    refMap.current.delete(key);
  }, []);

  useEffect(() => {
    if (!user?.username) {
      setDraftReady(false);
      return;
    }

    setDraftReady(false);

    const rawDraft = localStorage.getItem(draftKey);
    if (!rawDraft) {
      setDraftReady(true);
      return;
    }

    try {
      const parsedDraft = JSON.parse(rawDraft);
      const draftFormData = parsedDraft?.formData;
      const draftItems = parsedDraft?.selectedItems;
      const draftNewItem = parsedDraft?.newItem;
      const draftSelectedKategoriId = parsedDraft?.selectedKategoriId;
      const draftQtyMap = parsedDraft?.stokMenipisQtyMap;
      const draftUnitMap = parsedDraft?.stokMenipisUnitMap;

      if (draftFormData && typeof draftFormData === "object") {
        setFormData({
          keterangan:
            typeof draftFormData.keterangan === "string"
              ? draftFormData.keterangan
              : "",
          tanggal_penerimaan:
            typeof draftFormData.tanggal_penerimaan === "string" &&
            draftFormData.tanggal_penerimaan
              ? draftFormData.tanggal_penerimaan
              : getTomorrowDate(),
        });
      }

      if (Array.isArray(draftItems)) {
        const restoredItems = draftItems
          .filter(
            (item) =>
              item &&
              typeof item.barang_id === "string" &&
              item.barang_id.trim() !== "" &&
              Number.isFinite(Number(item.qty)) &&
              Number(item.qty) > 0
          )
          .map((item) => ({
            barang_id: item.barang_id,
            qty: Number(item.qty),
            qty_input: Number(item.qty_input ?? item.qty),
            satuan_input:
              typeof item.satuan_input === "string" ? item.satuan_input : "",
            keterangan: typeof item.keterangan === "string" ? item.keterangan : "",
          }));

        setSelectedItems(restoredItems);
      }

      if (draftNewItem && typeof draftNewItem === "object") {
        setNewItem({
          barang_id:
            typeof draftNewItem.barang_id === "string"
              ? draftNewItem.barang_id
              : "",
          qty:
            typeof draftNewItem.qty === "string" ||
            typeof draftNewItem.qty === "number"
              ? String(draftNewItem.qty)
              : "",
          satuan_input:
            typeof draftNewItem.satuan_input === "string"
              ? draftNewItem.satuan_input
              : "",
          keterangan:
            typeof draftNewItem.keterangan === "string"
              ? draftNewItem.keterangan
              : "",
        });
      }

      if (typeof draftSelectedKategoriId === "string" && draftSelectedKategoriId) {
        setSelectedKategoriId(draftSelectedKategoriId);
      }

      if (draftQtyMap && typeof draftQtyMap === "object") {
        const restoredQtyMap = Object.entries(draftQtyMap).reduce(
          (acc, [barangId, qty]) => {
            if (
              typeof barangId === "string" &&
              barangId.trim() !== "" &&
              (typeof qty === "string" || typeof qty === "number")
            ) {
              acc[barangId] = String(qty);
            }
            return acc;
          },
          {}
        );
        setStokMenipisQtyMap(restoredQtyMap);
      }

      if (draftUnitMap && typeof draftUnitMap === "object") {
        const restoredUnitMap = Object.entries(draftUnitMap).reduce(
          (acc, [barangId, unit]) => {
            if (
              typeof barangId === "string" &&
              barangId.trim() !== "" &&
              typeof unit === "string"
            ) {
              acc[barangId] = unit;
            }
            return acc;
          },
          {}
        );
        setStokMenipisUnitMap(restoredUnitMap);
      }
    } catch {
      localStorage.removeItem(draftKey);
    } finally {
      setDraftReady(true);
    }
  }, [draftKey, user?.username]);

  useEffect(() => {
    if (!user?.username || !draftReady) {
      return;
    }

    const draftPayload = {
      formData,
      selectedItems,
      newItem,
      selectedKategoriId,
      stokMenipisQtyMap,
      stokMenipisUnitMap,
    };

    localStorage.setItem(draftKey, JSON.stringify(draftPayload));
  }, [
    draftKey,
    user?.username,
    draftReady,
    formData,
    selectedItems,
    newItem,
    selectedKategoriId,
    stokMenipisQtyMap,
    stokMenipisUnitMap,
  ]);

  useEffect(() => {
    if (barangMenipis.length === 0) {
      return;
    }

    setStokMenipisQtyMap((prev) => {
      const next = { ...prev };
      for (const barang of barangMenipis) {
        if (next[barang.barang_id] === undefined) {
          next[barang.barang_id] = String(barang.suggested_qty);
        }
      }
      return next;
    });

    setStokMenipisUnitMap((prev) => {
      const next = { ...prev };
      for (const barang of barangMenipis) {
        if (next[barang.barang_id] === undefined) {
          next[barang.barang_id] = String(barang.satuan || "").toLowerCase();
        }
      }
      return next;
    });
  }, [barangMenipis]);

  useEffect(() => {
    if (!newItem.barang_id) {
      return;
    }

    const existsInFiltered = allBarang.some((barang) => {
      if (!barang.aktif || barang.barang_id !== newItem.barang_id) {
        return false;
      }
      if (selectedKategoriId !== "all" && barang.kategori_id !== selectedKategoriId) {
        return false;
      }
      return true;
    });

    if (!existsInFiltered) {
      setNewItem({ barang_id: "", qty: "", satuan_input: "", keterangan: "" });
    }
  }, [newItem.barang_id, allBarang, selectedKategoriId]);

  const fetchPemesananHistory = useCallback(async ({ append = false, baseOffset = 0 } = {}) => {
    const nextOffset = append ? baseOffset : 0;
    setLoadingMorePemesanan(true);
    try {
      const pemesananRes = await axios.get(`${API}/pemesanan`, {
        params: {
          limit: HISTORY_PAGE_SIZE,
          offset: nextOffset,
        },
      });
      const rows = pemesananRes.data || [];
      const totalFromHeader = Number(pemesananRes.headers?.["x-total-count"]);
      setPemesananList((prev) => (append ? [...prev, ...rows] : rows));
      const updatedOffset = nextOffset + rows.length;
      setPemesananOffset(updatedOffset);
      if (Number.isFinite(totalFromHeader)) {
        setTotalPemesanan(totalFromHeader);
        setHasMorePemesanan(updatedOffset < totalFromHeader);
      } else {
        setHasMorePemesanan(rows.length === HISTORY_PAGE_SIZE);
      }
    } catch (error) {
      toast.error("Gagal memuat riwayat pemesanan");
    } finally {
      setLoadingMorePemesanan(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [menimpisRes, barangRes, suppliersRes, kategoriRes] =
        await Promise.all([
          axios.get(`${API}/barang/stok-menipis`),
          axios.get(`${API}/barang`),
          axios.get(`${API}/supplier`),
          axios.get(`${API}/kategori`),
        ]);
      setBarangMenipis(menimpisRes.data);
      setAllBarang(barangRes.data);
      setSuppliers(suppliersRes.data.filter((s) => s.aktif));
      setKategori(kategoriRes.data);
      await fetchPemesananHistory({ append: false, baseOffset: 0 });
    } catch (error) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, [fetchPemesananHistory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleManualAdd = (barang, qty, satuanInput) => {
    const nextErrors = {};

    if (!barang) {
      nextErrors.barang_id = "Barang tidak ditemukan atau sudah tidak aktif.";
    }

    const qtyInput = Number(qty);
    if (!Number.isFinite(qtyInput) || qtyInput <= 0) {
      nextErrors.qty = "Masukkan qty yang valid dan lebih dari 0.";
    }

    const resolvedSatuanInput = String(satuanInput || barang?.satuan || "").toLowerCase();
    if (!resolvedSatuanInput) {
      nextErrors.satuan_input = "Pilih satuan qty terlebih dahulu.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setManualItemErrors(nextErrors);
      return;
    }

    const faktor = getBarangKonversiFactor(barang, resolvedSatuanInput);
    if (!Number.isFinite(faktor) || faktor <= 0) {
      setManualItemErrors({ satuan_input: "Satuan item belum terkonfigurasi di master barang." });
      return;
    }

    const qtyKonversi = qtyInput * faktor;

    const existing = selectedItems.find((i) => i.barang_id === barang.barang_id);
    if (existing) {
      setSelectedItems(
        selectedItems.map((i) =>
          i.barang_id === barang.barang_id
            ? {
                ...i,
                qty: qtyKonversi,
                qty_input: qtyInput,
                satuan_input: resolvedSatuanInput,
              }
            : i
        )
      );
    } else {
      setSelectedItems([
        ...selectedItems,
        {
          barang_id: barang.barang_id,
          qty: qtyKonversi,
          qty_input: qtyInput,
          satuan_input: resolvedSatuanInput,
          keterangan: "",
        },
      ]);
    }

    setFormErrors((prev) => ({ ...prev, items: "", tanggal_penerimaan: "" }));
    setManualItemErrors({});
  };

  const handleRemoveItem = (barangId) => {
    setSelectedItems(selectedItems.filter((i) => i.barang_id !== barangId));
    requestAnimationFrame(() => {
      barangInputRef.current?.focus();
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (selectedItems.length === 0) {
      nextErrors.items = "Pilih minimal 1 barang untuk dipesan.";
    }
    if (!String(formData.tanggal_penerimaan || "").trim()) {
      nextErrors.tanggal_penerimaan = "Tanggal penerimaan wajib dipilih.";
    }
    setFormErrors((prev) => ({ ...prev, ...nextErrors }));
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Konfirmasi Simpan Pemesanan",
      description: "Apakah Anda yakin ingin menyimpan pemesanan ini?",
      confirmText: "Ya, Simpan",
      destructive: false,
      onConfirm: async () => {
        try {
          await axios.post(`${API}/pemesanan`, {
            supplier_id: null,
            items: selectedItems,
            keterangan: formData.keterangan,
            tanggal_penerimaan: formData.tanggal_penerimaan,
          });
          toast.success("Pemesanan berhasil dibuat");
          setDialogOpen(false);
          setSelectedItems([]);
          setFormData({ 
            keterangan: "",
            tanggal_penerimaan: getTomorrowDate(),
          });
          setSelectedKategoriId("all");
          setNewItem({ barang_id: "", qty: "", satuan_input: "", keterangan: "" });
          setStokMenipisQtyMap({});
          setStokMenipisUnitMap({});
          if (user?.username) {
            localStorage.removeItem(draftKey);
          }
          fetchData();
        } catch (error) {
          toast.error(getApiErrorMessage(error, "Gagal membuat pemesanan"));
        }
      },
    });
  };

  const handleAddManualItem = () => {
    const qty = parseFloat(newItem.qty);
    const selectedBarang = filteredManualBarang.find((b) => b.barang_id === newItem.barang_id);

    if (!newItem.barang_id) {
      setManualItemErrors({ barang_id: "Pilih barang terlebih dahulu." });
      return;
    }
    if (!qty || qty <= 0) {
      setManualItemErrors((prev) => ({ ...prev, qty: "Masukkan qty yang valid." }));
      return;
    }

    const satuanInput = newItem.satuan_input || selectedBarang?.satuan || "";
    if (!satuanInput) {
      setManualItemErrors((prev) => ({ ...prev, satuan_input: "Pilih satuan qty terlebih dahulu." }));
      return;
    }

    handleManualAdd(selectedBarang, qty, satuanInput);
    toast.success("Barang ditambahkan");
    setNewItem({ barang_id: "", qty: "", satuan_input: "", keterangan: "" });
    setSearchManualBarang("");
    requestAnimationFrame(() => {
      barangInputRef.current?.focus();
    });
  };

  const handleUpdateStatus = async (pemesananId, newStatus) => {
    setConfirmDialog({
      open: true,
      title: "Konfirmasi Perbarui Status",
      description: `Apakah Anda yakin ingin memperbarui status menjadi ${newStatus}?`,
      confirmText: "Ya, Perbarui",
      destructive: false,
      onConfirm: async () => {
        try {
          await axios.put(`${API}/pemesanan/${pemesananId}`, { status: newStatus });
          toast.success(`Status diubah menjadi ${newStatus}`);
          fetchData();
        } catch (error) {
          toast.error(getApiErrorMessage(error, "Gagal update status"));
        }
      },
    });
  };

  const handleDeletePemesanan = async (pemesananId) => {
    setConfirmDialog({
      open: true,
      title: "Konfirmasi Hapus Pemesanan",
      description: "Apakah Anda yakin ingin menghapus pemesanan ini? Tindakan ini tidak dapat dibatalkan.",
      confirmText: "Ya, Hapus",
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/pemesanan/${pemesananId}`);
          toast.success("Pemesanan berhasil dihapus");
          fetchData();
        } catch (error) {
          toast.error(getApiErrorMessage(error, "Gagal menghapus pemesanan"));
        }
      },
    });
  };

  const handleDownloadPDF = async (pemesanan) => {
    try {
      const pemesananId = pemesanan?.pemesanan_id;
      if (!pemesananId) {
        toast.error("ID pemesanan tidak valid");
        return;
      }

      const response = await axios.get(`${API}/pemesanan/${pemesananId}/pdf?t=${Date.now()}`, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getPemesananDownloadFilename(pemesanan));
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF berhasil didownload");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Gagal download PDF"));
    }
  };

  const handleViewDetail = (pemesanan) => {
    setSelectedPemesanan(pemesanan);
    setDetailDialog(true);
  };

  const getBarangName = useCallback((barangId) => {
    const barang = allBarang.find((b) => b.barang_id === barangId);
    return barang ? barang.nama_barang : "-";
  }, [allBarang]);

  const getSupplierName = useCallback((supplierId) => {
    const supplier = suppliers.find((s) => s.supplier_id === supplierId);
    return supplier ? supplier.nama_supplier : "Belum ditentukan";
  }, [suppliers]);

  const getBarangDetails = (barangId) => {
    return allBarang.find((b) => b.barang_id === barangId);
  };

  const historyStatusOptions = useMemo(() => {
    const seen = new Set();
    return pemesananList
      .map((item) => String(item?.status || "").trim())
      .filter((status) => {
        if (!status || seen.has(status)) {
          return false;
        }
        seen.add(status);
        return true;
      });
  }, [pemesananList]);

  const filteredPemesananHistory = useMemo(() => {
    const keyword = historySearchTerm.trim().toLowerCase();

    return pemesananList.filter((entry) => {
      const statusMatches =
        historyStatusFilter === "all" || String(entry?.status || "") === historyStatusFilter;

      if (!statusMatches) {
        return false;
      }

      if (!keyword) {
        return true;
      }

      const searchableParts = [
        entry?.pemesanan_id,
        entry?.dibuat_oleh,
        entry?.keterangan,
        getSupplierName(entry?.supplier_id),
        ...(entry?.items || []).map((item) => getBarangName(item?.barang_id)),
      ];

      return searchableParts
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(keyword);
    });
  }, [getBarangName, getSupplierName, historySearchTerm, historyStatusFilter, pemesananList]);

  const isHistorySearchActive = Boolean(historySearchTerm.trim() || historyStatusFilter !== "all");

  const getKategoriName = (kategoriId) => {
    const kat = kategori.find((k) => k.kategori_id === kategoriId);
    return kat ? kat.nama_kategori : "-";
  };

  const filteredBarangMenipis = barangMenipis.filter((barang) => {
    if (selectedKategoriId === "all") {
      return true;
    }
    return barang.kategori_id === selectedKategoriId;
  });

  const filteredManualBarang = allBarang.filter((barang) => {
    if (!barang.aktif) {
      return false;
    }
    if (selectedKategoriId !== "all" && barang.kategori_id !== selectedKategoriId) {
      return false;
    }
    return true;
  });

  const kategoriOptions = kategori.filter((kat) => kat.aktif);

  if (loading) {
    return <PageLoadingState label="Memuat pemesanan barang" description="Menyiapkan stok menipis, kategori, dan riwayat pemesanan terbaru." />;
  }

  return (
    <PageShell data-testid="pemesanan-page">
      <PageHero
        tone="sky"
        eyebrow="Perencanaan Stok"
        title="Pemesanan Barang Dapur"
        description="Susun pemesanan barang dari stok menipis maupun input manual dengan tampilan yang lebih terstruktur."
        actions={(
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="shrink-0"
          >
            <Home size={16} />
            Dashboard
          </Button>
        )}
      />

      {barangMenipis.length > 0 && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-orange-600 mt-0.5" size={20} />
            <div className="flex-1">
              <h3 className="font-manrope font-bold text-xl md:text-2xl text-orange-900">
                {barangMenipis.length} Barang Stok Menipis!
              </h3>
              <p className="text-sm text-orange-800 mt-1">
                Ada barang yang stoknya sudah mencapai atau di bawah minimum.
                Segera lakukan pemesanan.
              </p>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="manual" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2 bg-sky-100/80">
          <TabsTrigger value="manual" data-testid="tab-manual">
            Pemesanan Manual
          </TabsTrigger>
          <TabsTrigger value="riwayat" data-testid="tab-riwayat">
            Riwayat Pemesanan
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manual" className="space-y-6">
          <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-6 shadow-sm space-y-4">
            <h2 className="font-manrope font-extrabold text-2xl md:text-[1.75rem] text-sky-900">Filter Kategori Barang</h2>
            <div className="space-y-2">
              <Label>Kategori</Label>
              <Select
                value={selectedKategoriId}
                onValueChange={(value) => setSelectedKategoriId(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pilih kategori" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Kategori</SelectItem>
                  {kategoriOptions.map((kat) => (
                    <SelectItem key={kat.kategori_id} value={kat.kategori_id}>
                      {kat.nama_kategori}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className={getFieldMessageClass()}>
              Filter kategori membantu mempercepat pemilihan barang tanpa mengubah daftar stok yang tersedia.
            </p>
          </div>

          <div className="rounded-xl border border-orange-200 bg-orange-50/30 p-6 shadow-sm">
            <h2 className="font-manrope font-extrabold text-2xl md:text-[1.75rem] mb-6 text-orange-900">
              Daftar Barang Stok Menipis
            </h2>
            {filteredBarangMenipis.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">
                Tidak ada barang stok menipis untuk filter saat ini
              </p>
            ) : (
              <div className="space-y-4">
                {filteredBarangMenipis.map((barang) => (
                  <div
                    key={barang.barang_id}
                    className="flex items-center justify-between p-4 border rounded-md hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <h3 className="font-manrope font-semibold">
                        {barang.nama_barang}
                      </h3>
                      <div className="flex gap-4 mt-1 text-sm text-muted-foreground">
                        <span>Kategori: {getKategoriName(barang.kategori_id)}</span>
                        <span>
                          Stok: {formatStockValue(barang.stok_current)} / Min: {barang.min_stok}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        ref={(node) => setMappedRef(quickAddQtyRefs, barang.barang_id, node)}
                        type="number"
                        placeholder="Qty"
                        className="w-24"
                        value={
                          stokMenipisQtyMap[barang.barang_id] ??
                          String(barang.suggested_qty)
                        }
                        onChange={(e) =>
                          setStokMenipisQtyMap((prev) => ({
                            ...prev,
                            [barang.barang_id]: e.target.value,
                          }))
                        }
                        onKeyDown={(event) =>
                          handleArrowFieldKeyDown(event, {
                            onEnter: () => quickAddButtonRefs.current.get(barang.barang_id)?.click(),
                            onRight: () => quickAddSatuanRefs.current.get(barang.barang_id)?.focus(),
                          })
                        }
                      />
                      <Select
                        value={
                          stokMenipisUnitMap[barang.barang_id] ||
                          String(barang.satuan || "").toLowerCase()
                        }
                        onValueChange={(value) =>
                          {
                            setStokMenipisUnitMap((prev) => ({
                              ...prev,
                              [barang.barang_id]: value,
                            }));
                            requestAnimationFrame(() => {
                              quickAddButtonRefs.current.get(barang.barang_id)?.focus();
                            });
                          }
                        }
                      >
                        <SelectTrigger
                        ref={(node) => setMappedRef(quickAddSatuanRefs, barang.barang_id, node)}
                        className="w-28"
                        onKeyDown={(event) =>
                          handleArrowFieldKeyDown(event, {
                            onLeft: () => quickAddQtyRefs.current.get(barang.barang_id)?.focus(),
                            onRight: () => quickAddButtonRefs.current.get(barang.barang_id)?.focus(),
                          })
                        }
                      >
                          <SelectValue placeholder="Satuan" />
                        </SelectTrigger>
                        <SelectContent>
                          {getBarangUnits(barang).map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                      ref={(node) => setMappedRef(quickAddButtonRefs, barang.barang_id, node)}
                      size="sm"
                      onKeyDown={(event) =>
                        handleArrowFieldKeyDown(event, {
                          onLeft: () => quickAddSatuanRefs.current.get(barang.barang_id)?.focus(),
                        })
                      }
                      onClick={() => {
                        const qty = parseFloat(
                          stokMenipisQtyMap[barang.barang_id] ??
                              String(barang.suggested_qty)
                          );
                          const satuanInput =
                            stokMenipisUnitMap[barang.barang_id] ||
                            String(barang.satuan || "").toLowerCase();
                          if (!qty || qty <= 0) {
                            toast.error("Masukkan qty yang valid");
                            return;
                          }
                          handleManualAdd(barang, qty, satuanInput);
                          toast.success(`${barang.nama_barang} ditambahkan`);
                          requestAnimationFrame(() => {
                            quickAddQtyRefs.current.get(barang.barang_id)?.focus();
                          });
                        }}
                      >
                        <Plus size={16} className="mr-1" />
                        Tambah
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-indigo-200 bg-indigo-50/30 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-manrope font-extrabold text-2xl md:text-[1.75rem] text-indigo-900">
                Tambah Barang Lain
              </h2>
            </div>
            <div className="space-y-4">
              <InlineHint className="border-indigo-200/80 bg-white/70">
                Barang yang sama akan diperbarui qty-nya, bukan diduplikasi, sehingga daftar pemesanan tetap rapi.
              </InlineHint>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label>Pilih Barang</Label>
                  <SearchableDropdown
                    value={newItem.barang_id}
                    searchValue={searchManualBarang}
                    onSearchValueChange={setSearchManualBarang}
                    onValueChange={(value) => {
                      const barang = filteredManualBarang.find((b) => b.barang_id === value);
                      if (barang) {
                        const existingItem = selectedItems.find(
                          (i) => i.barang_id === value
                        );
                        if (existingItem) {
                          toast.warning("Barang sudah ada di list");
                        } else {
                          setNewItem({
                            barang_id: value,
                            qty: 1,
                            satuan_input: String(barang.satuan || "").toLowerCase(),
                            keterangan: "",
                          });
                          setManualItemErrors((prev) => ({ ...prev, barang_id: "", qty: "", satuan_input: "" }));
                          setSearchManualBarang("");
                          focusFieldNextFrame(manualQtyInputRef);
                        }
                      }
                    }}
                    error={manualItemErrors.barang_id}
                    helperText="Ketik nama barang untuk menambahkan item manual di luar daftar stok menipis."
                    selectedLabel="Barang manual"
                    selectedPlaceholder="Cari dan pilih barang..."
                    emptyText="Tidak ada barang yang cocok"
                    inputTestId="select-barang-manual"
                    inputRef={barangInputRef}
                    items={filteredManualBarang.map((barang) => ({
                      value: barang.barang_id,
                      label: `${barang.nama_barang} - Stok: ${formatStockValue(barang.stok_current)}`,
                      searchText: barang.nama_barang,
                    }))}
                  />
                </div>
                <div>
                  <Label>Qty</Label>
                  <div className="flex gap-2">
                    <Input
                      ref={manualQtyInputRef}
                      data-testid="input-qty-manual"
                      type="number"
                      min="1"
                      placeholder="Qty"
                      value={newItem.qty}
                      onChange={(e) =>
                        {
                          setNewItem({
                            ...newItem,
                            qty: e.target.value,
                          });
                          setManualItemErrors((prev) => ({ ...prev, qty: "" }));
                        }
                      }
                      onKeyDown={(event) =>
                        handleArrowFieldKeyDown(event, {
                          onEnter: handleAddManualItem,
                          onLeft: () => barangInputRef.current?.focus(),
                          onRight: () => manualSatuanSelectRef.current?.focus(),
                        })
                      }
                      className={getFieldErrorClass(manualItemErrors.qty)}
                      aria-invalid={Boolean(manualItemErrors.qty)}
                    />
                    <Select
                      value={newItem.satuan_input}
                      onValueChange={(value) =>
                        {
                          setNewItem({
                            ...newItem,
                            satuan_input: value,
                          });
                          setManualItemErrors((prev) => ({ ...prev, satuan_input: "" }));
                          focusFieldNextFrame(manualAddButtonRef);
                        }
                      }
                      disabled={!newItem.barang_id}
                    >
                      <SelectTrigger
                        ref={manualSatuanSelectRef}
                        className={`w-28 ${getFieldErrorClass(manualItemErrors.satuan_input)}`}
                        aria-invalid={Boolean(manualItemErrors.satuan_input)}
                        onKeyDown={(event) =>
                          handleArrowFieldKeyDown(event, {
                            onLeft: () => manualQtyInputRef.current?.focus(),
                            onRight: () => manualAddButtonRef.current?.focus(),
                          })
                        }
                      >
                        <SelectValue placeholder="Satuan" />
                      </SelectTrigger>
                      <SelectContent>
                        {getBarangUnits(
                          filteredManualBarang.find((b) => b.barang_id === newItem.barang_id)
                        ).map((unit) => (
                          <SelectItem key={unit} value={unit}>
                            {unit}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      ref={manualAddButtonRef}
                      data-testid="add-item-manual"
                      onKeyDown={(event) =>
                        handleArrowFieldKeyDown(event, {
                          onLeft: () => manualSatuanSelectRef.current?.focus(),
                        })
                      }
                      onClick={handleAddManualItem}
                    >
                      <Plus size={16} className="mr-1" />
                      Tambah
                    </Button>
                  </div>
                  {manualItemErrors.qty ? <p className={getFieldMessageClass(true)}>{manualItemErrors.qty}</p> : null}
                  {manualItemErrors.satuan_input ? <p className={getFieldMessageClass(true)}>{manualItemErrors.satuan_input}</p> : null}
                </div>
              </div>
              {filteredManualBarang.length === 0 && (
                <p className="text-sm text-muted-foreground mt-3">
                  Tidak ada barang aktif yang cocok dengan kategori yang dipilih.
                </p>
              )}
              {formErrors.items ? <p className={getFieldMessageClass(true)}>{formErrors.items}</p> : null}
            </div>
          </div>

          {selectedItems.length > 0 && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/35 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-manrope font-extrabold text-2xl md:text-[1.75rem] text-sky-900">
                  Item Terpilih ({selectedItems.length})
                </h2>
                <Button
                  data-testid="submit-pemesanan-button"
                  onClick={() => setDialogOpen(true)}
                >
                  <Save size={16} className="mr-2" />
                  Buat Pemesanan
                </Button>
              </div>
              <div className="space-y-2">
                {selectedItems.map((item, index) => {
                  const barang = getBarangDetails(item.barang_id);
                  return (
                    <div
                      key={item.barang_id}
                      className="flex items-center justify-between p-3 border rounded-md"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-xs font-manrope font-bold text-sky-800">
                            {index + 1}
                          </span>
                          <p className="font-medium">{getBarangName(item.barang_id)}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Qty: {item.qty_input ?? item.qty} {item.satuan_input || barang?.satuan || ""}
                          {item.qty_input != null
                            ? ` (Qty dasar: ${item.qty} ${barang?.satuan || ""})`
                            : ""}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(item.barang_id)}
                      >
                        <Trash2 size={16} className="text-destructive" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <div className="border-t mt-4 pt-4">
                <div className="flex justify-between items-center">
                  <span className="font-manrope font-bold">Total Item:</span>
                  <span className="font-manrope font-bold text-xl text-primary">
                    {selectedItems.length} item
                  </span>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="riwayat" className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 overflow-hidden">
            {pemesananList.length === 0 ? (
              <p className="text-center py-12 text-muted-foreground">
                Belum ada riwayat pemesanan
              </p>
            ) : (
              <div className="space-y-4 p-6">
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_220px]">
                  <div>
                    <Label>Cari riwayat</Label>
                    <Input
                      type="text"
                      value={historySearchTerm}
                      onChange={(e) => setHistorySearchTerm(e.target.value)}
                      placeholder="Cari nomor, pembuat, supplier, atau nama barang"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={historyStatusFilter} onValueChange={setHistoryStatusFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Semua status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua status</SelectItem>
                        {historyStatusOptions.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setHistorySearchTerm("");
                      setHistoryStatusFilter("all");
                    }}
                    disabled={!isHistorySearchActive}
                  >
                    Reset Filter Riwayat
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Menampilkan {filteredPemesananHistory.length} hasil dari {pemesananList.length} data dimuat{totalPemesanan > 0 ? ` dari total ${totalPemesanan}` : ""}
                </p>
                {isHistorySearchActive && pemesananList.length > 0 && filteredPemesananHistory.length === 0 && hasMorePemesanan ? (
                  <p className="text-sm text-muted-foreground">
                    Belum ada hasil pada data yang sudah dimuat. Muat data tambahan atau ubah kata kunci pencarian.
                  </p>
                ) : null}
                {filteredPemesananHistory.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-border/80 bg-white/70 px-4 py-8 text-center text-muted-foreground">
                    Tidak ada hasil yang cocok dengan pencarian atau filter status.
                  </p>
                ) : filteredPemesananHistory.map((pemesanan) => (
                  <div
                    key={pemesanan.pemesanan_id}
                    className="border rounded-md p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-manrope font-semibold">
                            Pemesanan #{pemesanan.pemesanan_id.slice(0, 8)}
                          </h3>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              pemesanan.status === "Draft"
                                ? "bg-gray-100 text-gray-800"
                                : pemesanan.status === "Dipesan"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {pemesanan.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {new Date(pemesanan.tanggal).toLocaleDateString("id-ID", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}{" "}
                          - Dibuat oleh: {pemesanan.dibuat_oleh}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Supplier: {getSupplierName(pemesanan.supplier_id)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetail(pemesanan)}
                        >
                          <Eye size={16} className="mr-1" />
                          Detail
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadPDF(pemesanan)}
                        >
                          <Download size={16} className="mr-1" />
                          PDF
                        </Button>
                        {pemesanan.status === "Draft" && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleUpdateStatus(pemesanan.pemesanan_id, "Dipesan")
                              }
                            >
                              <Check size={16} className="mr-1" />
                              Tandai Dipesan
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleDeletePemesanan(pemesanan.pemesanan_id)
                              }
                            >
                              <Trash2 size={16} className="text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1 mb-3">
                      {pemesanan.items.map((item, idx) => (
                        <p key={idx} className="text-sm">
                          {idx + 1}. {getBarangName(item.barang_id)} ({item.qty_input ?? item.qty} {item.satuan_input || getBarangDetails(item.barang_id)?.satuan || ""})
                          {item.keterangan && ` - ${item.keterangan}`}
                        </p>
                      ))}
                    </div>
                    {pemesanan.keterangan && (
                      <p className="text-sm text-muted-foreground italic">
                        {pemesanan.keterangan}
                      </p>
                    )}
                    <div className="border-t pt-3 mt-3">
                      <p className="font-manrope font-bold text-primary">
                        Total Item: {pemesanan.items.length}
                      </p>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={!hasMorePemesanan || loadingMorePemesanan}
                  onClick={() => fetchPemesananHistory({ append: true, baseOffset: pemesananOffset })}
                >
                  {loadingMorePemesanan
                    ? "Memuat..."
                    : hasMorePemesanan
                      ? "Muat Lebih Banyak"
                      : "Semua data sudah ditampilkan"}
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konfirmasi Pemesanan Barang</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className={getFieldMessageClass()}>
              Periksa kembali tanggal penerimaan dan total {selectedItems.length} item sebelum pemesanan dibuat.
            </p>
            <div className="space-y-2">
              <Label>Tanggal Penerimaan</Label>
              <Input
                ref={pemesananTanggalInputRef}
                data-testid="input-tanggal-penerimaan"
                type="date"
                value={formData.tanggal_penerimaan}
                onChange={(e) =>
                  {
                    setFormData({ ...formData, tanggal_penerimaan: e.target.value });
                    setFormErrors((prev) => ({ ...prev, tanggal_penerimaan: "" }));
                  }
                }
                className={getFieldErrorClass(formErrors.tanggal_penerimaan)}
                aria-invalid={Boolean(formErrors.tanggal_penerimaan)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    pemesananKeteranganRef.current?.focus();
                  }
                }}
              />
              {formErrors.tanggal_penerimaan ? (
                <p className={getFieldMessageClass(true)}>{formErrors.tanggal_penerimaan}</p>
              ) : (
                <p className={getFieldMessageClass()}>
                  Untuk pemesanan besok, pilih tanggal besok. Tanggal ini hanya mengatur rencana penerimaan, bukan alur approval.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Keterangan (Opsional)</Label>
              <Textarea
                ref={pemesananKeteranganRef}
                data-testid="input-keterangan-pemesanan"
                value={formData.keterangan}
                onChange={(e) =>
                  setFormData({ ...formData, keterangan: e.target.value })
                }
                rows={3}
                onKeyDown={(event) =>
                  handleArrowFieldKeyDown(event, {
                    onLeft: () => pemesananTanggalInputRef.current?.focus(),
                    onRight: () => pemesananConfirmButtonRef.current?.focus(),
                  })
                }
              />
            </div>

            <div className="bg-muted/50 p-4 rounded-md">
              <p className="font-manrope font-bold text-primary text-lg">
                Total Item: {selectedItems.length}
              </p>
            </div>

            <DialogFooter>
              <Button
                ref={pemesananCancelButtonRef}
                type="button"
                variant="outline"
                onKeyDown={(event) =>
                  handleArrowFieldKeyDown(event, {
                    onLeft: () => pemesananKeteranganRef.current?.focus(),
                    onRight: () => pemesananConfirmButtonRef.current?.focus(),
                  })
                }
                onClick={() => setDialogOpen(false)}
              >
                Batal
              </Button>
              <Button
                ref={pemesananConfirmButtonRef}
                data-testid="confirm-pemesanan-button"
                type="submit"
                onKeyDown={(event) =>
                  handleArrowFieldKeyDown(event, {
                    onLeft: () => pemesananCancelButtonRef.current?.focus(),
                  })
                }
              >
                Buat Pemesanan
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detail Pemesanan</DialogTitle>
          </DialogHeader>
          {selectedPemesanan && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Nomor Pemesanan</p>
                  <p className="font-manrope font-semibold">
                    #{selectedPemesanan.pemesanan_id.slice(0, 8)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      selectedPemesanan.status === "Draft"
                        ? "bg-gray-100 text-gray-800"
                        : selectedPemesanan.status === "Dipesan"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {selectedPemesanan.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tanggal</p>
                  <p className="font-medium">
                    {new Date(selectedPemesanan.tanggal).toLocaleDateString(
                      "id-ID",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Dibuat Oleh</p>
                  <p className="font-medium">{selectedPemesanan.dibuat_oleh}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Supplier</p>
                  <p className="font-medium">
                    {getSupplierName(selectedPemesanan.supplier_id)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Item</p>
                  <p className="font-manrope font-bold text-primary">
                    {selectedPemesanan.items.length} item
                  </p>
                </div>
              </div>

              {selectedPemesanan.keterangan && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Keterangan</p>
                  <p className="text-sm">{selectedPemesanan.keterangan}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-manrope font-semibold mb-2">
                  Daftar Item ({selectedPemesanan.items.length})
                </p>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-center w-14">No</th>
                        <th className="px-3 py-2 text-left">Barang</th>
                        <th className="px-3 py-2 text-center">Qty Input</th>
                        <th className="px-3 py-2 text-center">Qty Dasar</th>
                        <th className="px-3 py-2 text-left">Satuan</th>
                        <th className="px-3 py-2 text-left">Keterangan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPemesanan.items.map((item, idx) => {
                        const barang = getBarangDetails(item.barang_id);
                        return (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2 text-center font-medium">{idx + 1}</td>
                            <td className="px-3 py-2">
                              {getBarangName(item.barang_id)}
                            </td>
                            <td className="px-3 py-2 text-center">{item.qty_input ?? item.qty}</td>
                            <td className="px-3 py-2 text-center">{item.qty}</td>
                            <td className="px-3 py-2">
                              {item.satuan_input || barang?.satuan || '-'}
                            </td>
                            <td className="px-3 py-2 text-sm text-muted-foreground">
                              {item.keterangan || '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmActionDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, open, onConfirm: open ? prev.onConfirm : null }))
        }
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        destructive={confirmDialog.destructive}
        onConfirm={async () => {
          if (confirmDialog.onConfirm) {
            await confirmDialog.onConfirm();
          }
          setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
        }}
      />
    </PageShell>
  );
};

export default PemesananPage;
